import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useTheme } from '../context/ThemeContext';
import { getAssignedOrders } from '../services/api/picker';
import * as pickerService from '../services/api/picker';
import { getOrderDetails } from '../services/api/orders';
import { ArrowLeftIcon } from '../components/Icons';
import './History.css';
import './Dashboard.css';

const History = () => {
  const navigate = useNavigate();
  const { user } = useApp();
  const { theme } = useTheme();
  const [filter, setFilter] = useState('all');
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState({ completed_orders_count: 0, total_collected_plastic: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const [productNames, setProductNames] = useState({}); // Map of product_id to product_name

  const fetchOrders = useCallback(async (selectedFilter) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await getAssignedOrders(selectedFilter);
      
      if (result.success && result.data) {
        setOrders(result.data.orders || []);
        setSummary(result.data.summary || { completed_orders_count: 0, total_collected_plastic: 0 });
      } else {
        setError(result.message || 'Failed to load orders');
        setOrders([]);
        setSummary({ completed_orders_count: 0, total_collected_plastic: 0 });
      }
    } catch (err) {
      setError('Failed to load orders. Please try again.');
      setOrders([]);
      setSummary({ completed_orders_count: 0, total_collected_plastic: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch product names on component mount
  useEffect(() => {
    const fetchProductNames = async () => {
      try {
        const result = await pickerService.getProducts();
        if (result.success && result.data && Array.isArray(result.data)) {
          const names = {};
          result.data.forEach(product => {
            if (product.product_id) {
              const productId = product.product_id.toString();
              names[productId] = product.product_name || product.name || `Product ${productId}`;
            }
          });
          setProductNames(names);
        }
      } catch (error) {
        console.warn('Error fetching product names:', error);
      }
    };

    if (user) {
      fetchProductNames();
    }
  }, [user]);

  // Get product name helper function
  const getProductName = (product) => {
    // First try to get from productNames map (from products API)
    const productId = product.product_id?.toString();
    if (productId && productNames[productId]) {
      return productNames[productId];
    }
    
    // Fallback to product object from order details
    if (product.product && product.product.product_name) {
      return product.product.product_name;
    }
    if (product.product_name) {
      return product.product_name;
    }
    
    // Last fallback
    return product.category || `Product ${productId || 'Unknown'}`;
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchOrders(filter);
  }, [user, navigate, filter, fetchOrders]);

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  const formatOrderId = (id) => {
    if (!id) return '#000';
    return `#${id}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '₹0.00';
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  const getStatusLabel = (status) => {
    if (!status) return 'Unknown';
    const statusMap = {
      'COMPLETED': 'Completed',
      'DONE': 'Completed',
      'PAID': 'Paid',
      'PENDING': 'Pending',
      'ASSIGNED': 'Assigned',
      'IN_PROGRESS': 'In Progress',
      'REJECTED': 'Rejected',
      'CANCELLED': 'Cancelled'
    };
    return statusMap[status.toUpperCase()] || status;
  };

  const handleViewDetails = async (orderId) => {
    setShowOrderModal(true);
    setIsLoadingDetails(true);
    setDetailsError(null);
    setSelectedOrderDetails(null);

    try {
      const result = await getOrderDetails(orderId);
      if (result.success) {
        setSelectedOrderDetails(result.data);
      } else {
        setDetailsError(result.message || 'Failed to load order details');
      }
    } catch (error) {
      setDetailsError('Failed to load order details');
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const closeOrderModal = () => {
    setShowOrderModal(false);
    setSelectedOrderDetails(null);
    setDetailsError(null);
  };

  const filters = [
    { key: 'all', label: 'All' },
    { key: 'today', label: 'Today' },
    { key: 'week', label: 'This Week' },
    { key: 'month', label: 'This Month' }
  ];

  return (
    <div className="history-screen">
      <div className="history-wrapper">
        {/* Header */}
        <header className="history-header">
          <button className="back-btn" onClick={() => navigate('/dashboard')}>
            <ArrowLeftIcon size={20} color={theme === 'dark' ? '#ffffff' : '#0f172a'} />
          </button>
          <h1 className="page-title">Pickup History</h1>
          <div className="header-spacer"></div>
        </header>

        {/* Stats */}
        <div className="stats-row">
          <div className="stat-box">
            <span className="stat-number">{summary.completed_orders_count}</span>
            <span className="stat-label">Completed</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-box">
            <span className="stat-number">{summary.total_collected_plastic?.toFixed(1) || '0.0'}<small>kg</small></span>
            <span className="stat-label">Collected</span>
          </div>
        </div>

        {/* Filters */}
        <div className="filter-tabs">
          {filters.map((f) => (
            <button
              key={f.key}
              className={`filter-tab ${filter === f.key ? 'active' : ''}`}
              onClick={() => handleFilterChange(f.key)}
              disabled={loading}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="pickup-list">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading orders...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <div className="error-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="12" cy="16" r="1" fill="currentColor"/>
                </svg>
              </div>
              <p className="error-text">{error}</p>
              <button className="retry-btn" onClick={() => fetchOrders(filter)}>
                Try Again
              </button>
            </div>
          ) : orders.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="empty-title">No orders found</h3>
              <p className="empty-text">
                {filter === 'all' ? 'No orders yet' : `No orders for ${filter === 'today' ? 'today' : filter === 'week' ? 'this week' : 'this month'}`}
              </p>
            </div>
          ) : (
            orders.map((order) => (
              <div key={order.order_id} className="pickup-card">
                <div className="card-top">
                  <div className="order-info">
                    <span className="order-id">Order {formatOrderId(order.order_id)}</span>
                    <span className="order-date">
                      {formatShortDate(order.order_picked_date || order.order_created_at)}
                    </span>
                  </div>
                  <div className={`status-pill ${order.order_status?.toLowerCase()}`}>
                    <svg viewBox="0 0 24 24" fill="none" className="status-check">
                      <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {getStatusLabel(order.order_status)}
                  </div>
                </div>
                
                <div className="card-content">
                  {/* Customer Info */}
                  {order.user && (
                    <div className="customer-row">
                      <svg viewBox="0 0 24 24" fill="none" className="row-icon">
                        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="2"/>
                        <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      <span className="customer-name">{order.user.name}</span>
                    </div>
                  )}

                  {/* Address */}
                  {order.address && (
                    <div className="location-row">
                      <svg viewBox="0 0 24 24" fill="none" className="row-icon">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" stroke="currentColor" strokeWidth="2"/>
                        <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      <span className="location-text">
                        {order.address.location}{order.address.landmark ? `, ${order.address.landmark}` : ''}
                      </span>
                    </div>
                  )}
                  
                  {/* Products & Weight */}
                  <div className="details-row">
                    {order.order_products && order.order_products.length > 0 && (
                      <>
                        <div className="detail-item">
                          <svg viewBox="0 0 24 24" fill="none">
                            <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span>{order.order_products.length} item{order.order_products.length > 1 ? 's' : ''}</span>
                        </div>
                        <div className="detail-item">
                          <svg viewBox="0 0 24 24" fill="none">
                            <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <line x1="7" y1="7" x2="7.01" y2="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                          <span>
                            {order.order_products.reduce((sum, p) => sum + parseFloat(p.actual_quantity || p.quantity || 0), 0).toFixed(1)} kg
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <button 
                  className="view-details-btn"
                  onClick={() => handleViewDetails(order.order_id)}
                >
                  View Details
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Order Details Modal - Same as Dashboard */}
      {showOrderModal && (
        <div className="order-modal-overlay" onClick={closeOrderModal}>
          <div className="order-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="order-modal-header">
              <h2 className="order-modal-title">Order Details</h2>
              <button className="order-modal-close" onClick={closeOrderModal}>×</button>
            </div>

            <div className="order-modal-body">
              {isLoadingDetails ? (
                <div className="order-modal-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading order details...</p>
                </div>
              ) : detailsError ? (
                <div className="order-modal-error">
                  <p>{detailsError}</p>
                  <button className="btn-retry" onClick={() => handleViewDetails(selectedOrderDetails?.id)}>
                    Retry
                  </button>
                </div>
              ) : selectedOrderDetails ? (
                <div className="order-details-content">
                  {/* Order Status Banner */}
                  <div className={`order-status-banner ${selectedOrderDetails.order_status?.toLowerCase()}`}>
                    <span className="status-label">Status</span>
                    <span className="status-value">{selectedOrderDetails.order_status || 'N/A'}</span>
                  </div>

                  {/* Order Info Section */}
                  <div className="detail-section">
                    <h3 className="section-heading">Order Information</h3>
                    <div className="detail-grid">
                      <div className="detail-row">
                        <span className="detail-label">Order ID</span>
                        <span className="detail-value">#{selectedOrderDetails.order_id}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Status</span>
                        <span className={`detail-value status-badge ${selectedOrderDetails.order_status?.toLowerCase()}`}>
                          {selectedOrderDetails.order_status || 'N/A'}
                        </span>
                      </div>
                      {selectedOrderDetails.schedule_period && (
                        <div className="detail-row">
                          <span className="detail-label">Schedule</span>
                          <span className="detail-value">{selectedOrderDetails.schedule_period}</span>
                        </div>
                      )}
                      {selectedOrderDetails.order_picked_date && (
                        <div className="detail-row">
                          <span className="detail-label">Picked Date</span>
                          <span className="detail-value">{formatDate(selectedOrderDetails.order_picked_date)}</span>
                        </div>
                      )}
                      <div className="detail-row">
                        <span className="detail-label">Created</span>
                        <span className="detail-value">{formatDate(selectedOrderDetails.order_created_at)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Summary Section */}
                  {selectedOrderDetails.summary && (
                    <div className="detail-section">
                      <h3 className="section-heading">Order Summary</h3>
                      <div className="summary-content">
                        {selectedOrderDetails.summary}
                      </div>
                    </div>
                  )}

                  {/* Picker Details Section */}
                  {selectedOrderDetails.picker && (
                    <div className="detail-section">
                      <h3 className="section-heading">Picker Details</h3>
                      <div className="detail-grid">
                        <div className="detail-row">
                          <span className="detail-label">Name</span>
                          <span className="detail-value">{selectedOrderDetails.picker.name || 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Phone</span>
                          <span className="detail-value">{selectedOrderDetails.picker.phone_number || 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Email</span>
                          <span className="detail-value">{selectedOrderDetails.picker.email || 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Status</span>
                          <span className={`detail-value status-badge ${selectedOrderDetails.picker.user_status?.toLowerCase()}`}>
                            {selectedOrderDetails.picker.user_status || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Products Section */}
                  {selectedOrderDetails.products && selectedOrderDetails.products.length > 0 && (
                    <div className="detail-section">
                      <h3 className="section-heading">Products</h3>
                      <div className="products-list">
                        {selectedOrderDetails.products.map((item, index) => (
                          <div key={item.id || index} className="product-item">
                            {item.image && (
                              <div className="product-image">
                                <img src={item.image} alt={getProductName(item)} />
                              </div>
                            )}
                            <div className="product-info">
                              <span className="product-name">
                                {getProductName(item)}
                              </span>
                            </div>
                            <div className="product-details">
                              <div className="product-qty">
                                <span className="qty-label">Quantity:</span>
                                <span className="qty-value">{item.actual_quantity || 0} kg</span>
                              </div>
                              <div className="product-price">
                                <span className="price-label">Actual Price:</span>
                                <span className="price-value">{formatCurrency(item.actual_price)}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Order Image Section */}
                  {selectedOrderDetails.order_image && (
                    <div className="detail-section">
                      <h3 className="section-heading">Order Image</h3>
                      <div className="order-image-container">
                        <img 
                          src={selectedOrderDetails.order_image} 
                          alt="Order" 
                          className="order-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.style.display = 'none';
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Price Summary */}
                  {selectedOrderDetails.products && selectedOrderDetails.products.length > 0 && (
                    <div className="detail-section payment-section">
                      <h3 className="section-heading">Price Summary</h3>
                      <div className="payment-grid">
                        {selectedOrderDetails.products.map((item, index) => (
                          <div key={item.id || index} className="payment-row">
                            <span className="payment-label">{getProductName(item)} ({item.actual_quantity || 0} kg)</span>
                            <span className="payment-value">{formatCurrency(item.actual_price)}</span>
                          </div>
                        ))}
                        <div className="payment-row total">
                          <span className="payment-label">Total Amount</span>
                          <span className="payment-value">
                            {formatCurrency(
                              selectedOrderDetails.products.reduce((sum, item) => 
                                sum + parseFloat(item.actual_price || 0), 0
                              )
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* User Note */}
                  {selectedOrderDetails.user_note && (
                    <div className="detail-section">
                      <h3 className="section-heading">Customer Note</h3>
                      <div className="instructions-content">
                        {selectedOrderDetails.user_note}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            <div className="order-modal-footer">
              <button className="btn-close-modal" onClick={closeOrderModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default History;
